import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { buttonClasses } from '@forecastify/ui';
import { useT, type MessageKey } from '../../i18n';
import { useSession } from '../../session';
import { AuthFrame } from './auth-frame';
import { takeLinkReturn, takeVerifier } from './pkce';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Where Google sends the browser back — item 2 / D4.                        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **`/auth/callback` HAS BEEN DECLARED IN `AUTH_PATHS` SINCE C4 AND ROUTED TO NOTHING.** It is
 * on GoTrue's `additional_redirect_urls` allow-list in `config.toml` and it was in the shared
 * constants — and no component ever rendered at it, so a provider round trip would have landed on
 * the catch-all. Exactly WP34-2's shape, which is why `auth-paths.ts` exists at all.
 *
 * ⚠️⚠️ **THE CODE IS EXCHANGED BY THE API, NOT HERE.** The browser holds a `?code=` it cannot spend
 * alone; `POST /auth/oauth/callback` mints the tokens inside the API, registers the session in our
 * registry and sets the httpOnly refresh cookie. `pkce.ts` carries the argument — a browser-held
 * refresh token would make per-session revocation unenforceable, which `hard-won.md` WP8i-1 spent
 * five work packages establishing.
 *
 * ⚠️ **It runs ONCE, guarded by a ref rather than by the dependency array.** React 18's strict mode
 * mounts effects twice in development, and an authorization code is single-use — the second call
 * would spend a code GoTrue has already invalidated and turn a successful sign-in into an error
 * screen, in development only, which is the worst place for a bug to live.
 *
 * ⚠️ **A failure is a screen with a way back, not a redirect.** Bouncing silently to `/sign-in`
 * after a failed provider round trip leaves somebody looking at a login form with no idea why they
 * are there, and they try again the same way.
 */

const REASON: Readonly<Record<string, MessageKey>> = {
  invalid_credentials: 'oauth.expired',
  rate_limited: 'signIn.rateLimited',
  unavailable: 'signIn.unavailable',
  no_membership: 'signIn.noWorkspace',
  /* 22 Oct 2026 · a Google sign-in for an address with no account here. */
  account_not_found: 'oauth.accountNotFound',
  /* 22 Oct 2026 · a connect, for a Google account another account here already holds. */
  identity_in_use: 'oauth.identityInUse',
};

export function OAuthCallbackScreen() {
  const t = useT();
  const session = useSession();
  const [params] = useSearchParams();
  const [failure, setFailure] = useState<string | null>(null);
  /* 22 Oct 2026 · set when this return is a CONNECT from the account screen (`pkce.ts`). */
  const [linkReturn] = useState(takeLinkReturn);
  const navigate = useNavigate();
  const spent = useRef(false);

  const code = params.get('code');
  /* ⚠️ The provider's own refusal, which arrives as a query parameter rather than as a code —
     somebody pressing *Cancel* on the consent screen comes back this way. */
  const denied = params.get('error');

  useEffect(() => {
    if (spent.current) return;
    spent.current = true;

    if (denied !== null) {
      /* 22 Oct 2026 · *Cancel* on the consent screen is `access_denied`: start again. Anything
         else is the login service failing on its side (a token exchange it could not make) —
         that is not an expired sign-in, and saying so sent somebody round the loop again. */
      takeVerifier();
      setFailure(denied === 'access_denied' ? 'invalid_credentials' : 'unavailable');
      return;
    }

    const verifier = takeVerifier();
    if (code === null || verifier === null) {
      /* ⚠️ A missing verifier means this tab did not start the flow — a bookmarked callback URL, a
         reopened tab, a link somebody was sent. It is not an error to explain in detail; it is a
         sign-in that has to start again. */
      setFailure('invalid_credentials');
      return;
    }

    if (linkReturn !== null) {
      /* A connect: the person is signed in already and stays so; the code only links. */
      void session
        .request('/auth/google/link', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, code_verifier: verifier }),
        })
        .then(async (response) => {
          if (response.ok) {
            void navigate(`${linkReturn}${linkReturn.includes('?') ? '&' : '?'}google=linked`, {
              replace: true,
            });
            return;
          }
          const body = (await response.json().catch(() => null)) as {
            error?: { code?: string };
          } | null;
          setFailure(body?.error?.code ?? 'unknown');
        })
        .catch(() => {
          setFailure('unavailable');
        });
      return;
    }

    void session.signInWithOAuth(code, verifier).then((result) => {
      if (!result.ok) setFailure(result.code ?? 'unknown');
    });
  }, [code, denied, session, linkReturn, navigate]);

  return (
    <AuthFrame screen="oauth-callback" title={t('app.name')}>
      {failure === null ? (
        <p className="text-muted-foreground text-center text-[12.5px]">
          {linkReturn === null ? t('oauth.working') : t('oauth.linking')}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <p role="alert" className="text-destructive text-center text-[12.5px]">
            {t(REASON[failure] ?? 'error.unknown')}
          </p>
          {/* ⚠️ A `<Link>` wearing the button's skin — `verify-email-screen.tsx` settled this:
              a control that NAVIGATES is an anchor, and `buttonClasses` is how it looks like the
              rest without a second hand-written class string to drift. */}
          <Link to={linkReturn ?? '/sign-in'} className={buttonClasses('default', 'lg', 'w-full')}>
            {linkReturn === null ? t('oauth.backToSignIn') : t('oauth.backToAccount')}
          </Link>
        </div>
      )}
    </AuthFrame>
  );
}
